import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import BuildingIcon from "@redotech/redo-icons/building-02.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useRef, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";

interface TeamOption {
  _id: string;
  name: string;
}

export interface TeamSearchBoxProps {
  value: string;
  setValue: (value: string) => void;
  label?: string;
  placeholder?: string;
  size?: "sm" | "md" | "lg";
  onFocus?: () => void;
  onBlur?: () => void;
  /** When true, a confirmed selection renders as a card instead of raw input text. */
  showSelectedCard?: boolean;
}

export const TeamSearchBox = memo(function TeamSearchBox({
  value,
  setValue,
  label = "Team",
  placeholder = "Search by team name or enter team ID...",
  size = "sm",
  onFocus,
  onBlur,
  showSelectedCard = false,
}: TeamSearchBoxProps) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [searchQuery, setSearchQuery] = useState("");
  const [teamOptions, setTeamOptions] = useState<TeamOption[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<TeamOption | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (value === "" && searchQuery !== "") {
      setSearchQuery("");
      setShowDropdown(false);
      setTeamOptions([]);
      setSelectedTeam(null);
    }
  }, [value, searchQuery]);

  // Restore display text when value is set externally (e.g. from URL params)
  useEffect(() => {
    async function restoreTeamName() {
      if (!value || searchQuery) {
        return;
      }
      setSearchQuery(value);
      setIsSearching(true);
      try {
        const result = await rpcClient.searchTeams({ query: value, limit: 1 });
        const match = result.teams.find((t: TeamOption) => t._id === value);
        setSearchQuery(match ? `${match.name} (${match._id})` : value);
        setSelectedTeam(match ?? { _id: value, name: value });
      } catch {
        setSearchQuery(value);
      } finally {
        setIsSearching(false);
      }
    }
    void restoreTeamName();
    // Only run on mount when value is pre-populated
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchTeams = useHandler(async (query: string) => {
    if (query.trim().length < 2) {
      setTeamOptions([]);
      setShowDropdown(false);
      return;
    }

    setIsSearching(true);
    try {
      const result = await rpcClient.searchTeams({
        query: query.trim(),
        limit: 10,
      });
      setTeamOptions(result.teams);
      setShowDropdown(true);
    } catch (error) {
      console.error("Team search failed:", error);
      setTeamOptions([]);
      setShowDropdown(false);
    } finally {
      setIsSearching(false);
    }
  });

  const handleTeamSelect = useHandler((team: TeamOption) => {
    setValue(team._id);
    setSearchQuery(`${team.name} (${team._id})`);
    setSelectedTeam(team);
    setShowDropdown(false);
    setTeamOptions([]);
  });

  const handleClearSelection = useHandler(() => {
    setValue("");
    setSearchQuery("");
    setSelectedTeam(null);
    setShowDropdown(false);
    setTeamOptions([]);
  });

  const handleInputChange = useHandler((inputValue: string) => {
    setSearchQuery(inputValue);
    setSelectedTeam(null);

    const parenthesesMatch = inputValue.match(/\(([^)]+)\)/);

    if (parenthesesMatch && parenthesesMatch[1]) {
      setValue(parenthesesMatch[1].trim());
    } else if (inputValue.trim()) {
      setValue(inputValue.trim());
    } else {
      setValue("");
    }

    // Debounce search — only user typing triggers searches
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (inputValue.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(
        () => void searchTeams(inputValue),
        300,
      );
    } else {
      setTeamOptions([]);
      setShowDropdown(false);
    }
  });

  if (showSelectedCard && selectedTeam && value) {
    return (
      <Flex dir="column" gap="xs" w="full">
        {label && (
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {label}
          </Text>
        )}
        <Flex
          align="center"
          bgColor="secondary"
          borderColor="brand"
          borderRadius="lg"
          borderStyle="solid"
          borderWidth="1px"
          gap="md"
          justify="space-between"
          p="md"
        >
          <Flex align="center" gap="md">
            <RedoFeaturedIcon color="brand" Icon={BuildingIcon} size="sm" />
            <Flex dir="column">
              <Text fontSize="sm" fontWeight="semibold">
                {selectedTeam.name}
              </Text>
              <Text fontSize="xs" textColor="secondary">
                {selectedTeam._id}
              </Text>
            </Flex>
          </Flex>
          <RedoButton
            hierarchy="tertiary"
            onClick={handleClearSelection}
            size="xs"
            text="Change"
          />
        </Flex>
      </Flex>
    );
  }

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <RedoTextInput
        label={label}
        onBlur={onBlur}
        onFocus={onFocus}
        placeholder={placeholder}
        setValue={handleInputChange}
        size={size}
        value={searchQuery}
      />
      {isSearching && (
        <Flex align="center" pt="sm">
          <RedoSpinner size="xs" />
        </Flex>
      )}
      {showDropdown && teamOptions.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            backgroundColor: "var(--redo-colors-background-bg-primary)",
            border: "1px solid #e1e5e9",
            borderRadius: "var(--border-radius-sm)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            zIndex: 1000,
            maxHeight: "200px",
            overflowY: "auto",
          }}
        >
          {teamOptions.map((team) => (
            <div
              key={team._id}
              onClick={() => handleTeamSelect(team)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f8f9fa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "white";
              }}
              style={{
                padding: "var(--redo-spacing-md) var(--redo-spacing-lg)",
                cursor: "pointer",
                borderBottom: "1px solid #f5f5f5",
              }}
            >
              <Text fontSize="sm" fontWeight="medium">
                {team.name}
              </Text>
              <Text fontSize="xs" textColor="secondary">
                {team._id}
              </Text>
            </div>
          ))}
        </div>
      )}
      {searchQuery.trim() !== "" &&
        !value &&
        !showDropdown &&
        teamOptions.length === 0 &&
        !isSearching && (
          <Text fontSize="xs" textColor="secondary">
            No teams found matching your search.
          </Text>
        )}
    </div>
  );
});
